import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../../components/Navbar';
import WuxingDisplay from '../../components/WuxingDisplay';
import { baziAPI } from '../../api/api';
import { usersAPI } from '../../api/api';
import { calculateDayun, calculateDayunManual } from '../../utils/dayun-calculator';
import { calculateDiShi, calculateNaYin, calculateDiShiByDate, calculateNaYinByDate } from '../../utils/bazi-utils';
import { calculateHiddenGan, calculateWuxing } from '../../utils/wuxing-calculator';
import {
  TIAN_GAN,
  DI_ZHI,
  calculateLiunianRange,
  calculateLiuyue,
  formatDate
} from '../../utils/liunian-calculator';
import { calculateShenshaCategorized } from '../../utils/shensha-calculator';
import './BaziDetail.css';
import ShenShaCard from '../../components/ShenShaCard';
import ChartTable from '../../components/ChartTable';
import Drilldown, { MONTH_NAMES } from '../../components/Drilldown';
import ShareSettingsSection from '../../components/ShareSettingsSection';
import CommentsSection from '../../components/CommentsSection';
import BrandIcon from '../../components/BrandIcon';
import { BASE_URL, DEFAULT_AVATAR } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SecureImage from '../../components/SecureImage';
import TagSelect from '../../components/TagSelect';
import { Lunar } from 'lunar-javascript';

const ganzhiOfYear = (year) => ({
  gan: TIAN_GAN[(year - 4) % 10],
  zhi: DI_ZHI[(year - 4) % 12]
});

const NAV_SECTIONS = [
  { id: 'section-paipan', label: '排盘' },
  { id: 'section-yun', label: '大运流年' },
  { id: 'section-shensha', label: '神煞五行' },
  { id: 'section-ai', label: 'AI解析' },
  { id: 'section-comments', label: '评论' }
];

const BaziDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publisher, setPublisher] = useState(null);

  const { user } = useAuth();
  const getAvatarSrc = (u) => (u?.avatarUrl ? `${BASE_URL}${u.avatarUrl}` : DEFAULT_AVATAR);

  // 当前农历时刻（会话内稳定）
  const currentLunar = useMemo(() => Lunar.fromDate(new Date()), []);
  const currentYear = currentLunar.getYear();
  const currentMonthIndex = useMemo(() => {
    const arr = calculateLiuyue(currentYear, currentLunar);
    const i = arr.findIndex((m) => m.isCurrent);
    return i >= 0 ? i : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear]);

  // 钻取选择态
  const [selectedDayunIndex, setSelectedDayunIndex] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(null);
  const [liuyueData, setLiuyueData] = useState([]);

  // 命盘第 5/6/7 列显隐勾选（老版即有，改版回归）
  const [showDayunCol, setShowDayunCol] = useState(true);
  const [showLiunianCol, setShowLiunianCol] = useState(true);
  const [showLiuyueCol, setShowLiuyueCol] = useState(true);

  // 神煞 / 五行分析 tab
  const [analysisTab, setAnalysisTab] = useState('shensha');

  // 大运数据（本地计算）
  const [dayunData, setDayunData] = useState({ dayunList: [], qiyunAge: { years: 0, months: 0, days: 0 } });

  // 地势 / 纳音（四柱，沿用既有 lunar 库计算，保持排盘输出不变）
  const [diShiData, setDiShiData] = useState({ year: '', month: '', day: '', hour: '' });
  const [naYinData, setNaYinData] = useState({ year: '', month: '', day: '', hour: '' });

  // 神煞、五行（本地计算）
  const [shenshaData, setShenshaData] = useState({});
  const [wuxingData, setWuxingData] = useState({ jin: 0, mu: 0, shui: 0, huo: 0, tu: 0 });

  // 弹窗
  const [showShareSettingsModal, setShowShareSettingsModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [labelSelection, setLabelSelection] = useState([]);

  // 吸顶 / 锚点
  const heroRef = useRef(null);
  const [navH, setNavH] = useState(60);
  const [stuck, setStuck] = useState(false);
  const [activeSection, setActiveSection] = useState('section-paipan');

  useEffect(() => { fetchDetail(); /* eslint-disable-next-line */ }, [id]);

  // 流月随选中年份实时计算
  useEffect(() => {
    if (selectedYear) setLiuyueData(calculateLiuyue(selectedYear, currentLunar));
  }, [selectedYear, currentLunar]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await baziAPI.getById(id);
      const recordData = response.data.data;
      setRecord(recordData);
      if (recordData?.userId) {
        try {
          const uRes = await usersAPI.getById(recordData.userId);
          setPublisher(uRes.data.user);
        } catch (e) { /* ignore */ }
      }

      if (recordData.baziResult) {
        const hiddenGan = calculateHiddenGan(recordData.baziResult);
        setWuxingData(calculateWuxing(recordData.baziResult, hiddenGan));
      }

      if (recordData.gregorianDate && recordData.gregorianDate.year) {
        setDayunData(calculateDayun(recordData.gregorianDate, recordData.gender, recordData.baziResult.monthPillar));
        // 四柱地势 / 纳音沿用 lunar 库（保持排盘输出不变）
        setDiShiData(calculateDiShiByDate(recordData.gregorianDate));
        setNaYinData(calculateNaYinByDate(recordData.gregorianDate));
      } else if (recordData.baziResult && recordData.baziResult.monthPillar) {
        setDayunData(calculateDayunManual(
          recordData.baziResult.monthPillar,
          recordData.baziResult.yearPillar.gan,
          recordData.gender,
          new Date().getFullYear() - 30
        ));
        const b = recordData.baziResult;
        setDiShiData({
          year: calculateDiShi(b.yearPillar.gan, b.yearPillar.zhi),
          month: calculateDiShi(b.monthPillar.gan, b.monthPillar.zhi),
          day: calculateDiShi(b.dayPillar.gan, b.dayPillar.zhi),
          hour: calculateDiShi(b.hourPillar.gan, b.hourPillar.zhi)
        });
        setNaYinData({
          year: calculateNaYin(b.yearPillar.gan, b.yearPillar.zhi),
          month: calculateNaYin(b.monthPillar.gan, b.monthPillar.zhi),
          day: calculateNaYin(b.dayPillar.gan, b.dayPillar.zhi),
          hour: calculateNaYin(b.hourPillar.gan, b.hourPillar.zhi)
        });
      }

      if (recordData.baziResult) {
        const b = recordData.baziResult;
        setShenshaData(calculateShenshaCategorized(
          b.dayPillar.gan, b.yearPillar.zhi, b.monthPillar.zhi, b.dayPillar.zhi, b.hourPillar.zhi
        ));
      }
      setError('');
    } catch (err) {
      setError('加载失败，请重试');
      console.error('获取详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 当前大运下标
  const currentDayunIndex = useMemo(() => {
    const list = dayunData.dayunList || [];
    for (let i = 0; i < list.length; i++) {
      const sY = list[i].startYear;
      const nextSY = list[i + 1] && list[i + 1].startYear;
      if (sY && currentYear >= sY && (i === list.length - 1 || (nextSY && currentYear < nextSY))) return i;
    }
    return list.length ? 0 : null;
  }, [dayunData, currentYear]);

  // 数据加载后，默认钻取到当前时刻的完整路径
  useEffect(() => {
    if (!(dayunData.dayunList && dayunData.dayunList.length)) return;
    setSelectedDayunIndex(currentDayunIndex);
    setSelectedYear(currentYear);
    setSelectedMonthIndex(currentMonthIndex);
  }, [dayunData, currentDayunIndex, currentYear, currentMonthIndex]);

  // 选中大运的 10 个流年（纯展示包装，复用逐年干支公式）
  const dayunLiunianList = useMemo(() => {
    const list = dayunData.dayunList || [];
    if (selectedDayunIndex == null || !list[selectedDayunIndex]) return [];
    return calculateLiunianRange(list[selectedDayunIndex].startYear, 10, currentYear);
  }, [dayunData, selectedDayunIndex, currentYear]);

  const handleSelectDayun = useCallback((idx) => {
    const list = dayunData.dayunList || [];
    const yun = list[idx];
    setSelectedDayunIndex(idx);
    if (yun) {
      const y = yun.startYear;
      setSelectedYear(y);
      setSelectedMonthIndex(y === currentYear ? currentMonthIndex : 0);
    }
  }, [dayunData, currentYear, currentMonthIndex]);

  const handleSelectYear = useCallback((year) => {
    setSelectedYear(year);
    setSelectedMonthIndex(year === currentYear ? currentMonthIndex : 0);
  }, [currentYear, currentMonthIndex]);

  const handleSelectMonth = useCallback((idx) => setSelectedMonthIndex(idx), []);

  // 测量导航栏高度
  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.navbar');
      setNavH(nav ? nav.offsetHeight : 60);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [record]);

  // 吸顶：命主头滚出（底边越过导航线）后显示
  useEffect(() => {
    const onScroll = () => {
      const el = heroRef.current;
      if (!el) return;
      setStuck(el.getBoundingClientRect().bottom <= navH + 4);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [navH, record]);

  // 滚动监听高亮当前 section
  useEffect(() => {
    if (!record) return;
    const els = NAV_SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveSection(visible[0].target.id);
    }, { rootMargin: `-${navH + 70}px 0px -55% 0px`, threshold: [0, 0.15] });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [navH, record, dayunData]);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - (navH + 60);
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, [navH]);

  if (loading) {
    return (
      <div className="bazi-detail">
        <Navbar />
        <div className="container"><div className="loading-container">加载中...</div></div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="bazi-detail">
        <Navbar />
        <div className="container">
          <div className="error-container">
            <p>{error || '记录不存在'}</p>
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>返回</button>
          </div>
        </div>
      </div>
    );
  }

  const { baziResult } = record;
  const riGan = baziResult?.dayPillar?.gan;
  const isOwner = user && record?.userId && user.id === record.userId;

  const dayunList = dayunData.dayunList || [];
  const selDayun = selectedDayunIndex != null ? dayunList[selectedDayunIndex] : null;
  const dayunCol = selDayun ? { gan: selDayun.gan, zhi: selDayun.zhi } : null;
  const liunianCol = selectedYear ? { year: selectedYear, ...ganzhiOfYear(selectedYear) } : null;
  const liuyueCol = (selectedMonthIndex != null && liuyueData[selectedMonthIndex])
    ? { gan: liuyueData[selectedMonthIndex].gan, zhi: liuyueData[selectedMonthIndex].zhi, monthName: MONTH_NAMES[selectedMonthIndex] }
    : null;

  const eightChars = baziResult
    ? ['yearPillar', 'monthPillar', 'dayPillar', 'hourPillar'].map((k) => `${baziResult[k]?.gan || ''}${baziResult[k]?.zhi || ''}`)
    : [];

  return (
    <div className="bazi-detail">
      <Navbar />

      {/* 吸顶摘要 + 锚点导航 */}
      <div className={`bazi-sticky ${stuck ? 'visible' : ''}`} style={{ top: navH }}>
        <div className="bazi-sticky-inner">
          <div className="sticky-summary">
            <span className="sticky-name">{record.name || '命主'}</span>
            <span className="sticky-chars">
              {eightChars.map((gz, i) => <span key={i} className="sticky-gz">{gz}</span>)}
            </span>
          </div>
          <nav className="anchor-nav">
            {NAV_SECTIONS.map((s) => (
              <button
                key={s.id}
                className={`anchor-link ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => scrollToSection(s.id)}
              >{s.label}</button>
            ))}
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="detail-header">
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} className="inline-icon" /> 返回
          </button>
          <h1>八字详情</h1>
        </div>

        {/* 命主头 */}
        <div className="card hero-card" ref={heroRef}>
          <div className="hero-main">
            <div className="hero-idline">
              <h2 className="hero-name">{record.name || '未命名'}</h2>
              {record.gender && <span className={`hero-gender ${record.gender === '男' ? 'g-male' : 'g-female'}`}>{record.gender}</span>}
            </div>
            <div className="hero-dates">
              <span><i>公历</i>{formatDate(record.gregorianDate)}</span>
              {record.lunarDate && record.lunarDate.year && (
                <span><i>农历</i>{formatDate(record.lunarDate)}{record.lunarDate.isLeapMonth && ' (闰月)'}</span>
              )}
            </div>
            {Array.isArray(record.labels) && record.labels.length > 0 && (
              <div className="hero-labels">
                {record.labels.map((l) => (
                  <span key={l._id || l.name || l} className="hero-chip">{l.name || l}</span>
                ))}
              </div>
            )}
          </div>
          <div className="hero-side">
            <div className="hero-publisher">
              <span className="hero-publisher-label">发布者</span>
              <Link to={publisher?._id ? `/users/${publisher._id}` : '#'} className="publisher-link">
                <SecureImage className="avatar" src={getAvatarSrc(publisher)} alt="" />
              </Link>
              <Link to={publisher?._id ? `/users/${publisher._id}` : '#'} className="publisher-name">
                {publisher?.nickname || publisher?.username || '—'}
              </Link>
            </div>
            {isOwner && (
              <div className="hero-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setLabelSelection(Array.isArray(record.labels) ? record.labels.map((l) => l.name || l) : []);
                  setShowLabelsModal(true);
                }}>标签管理</button>
                <button className="btn btn-primary btn-sm" onClick={() => setShowShareSettingsModal(true)}>分享设置</button>
              </div>
            )}
          </div>
        </div>

        {/* 排盘主表 */}
        <div className="card" id="section-paipan">
          <div className="card-head-row">
            <h2>命盘</h2>
            <div className="col-toggles">
              <label><input className="styled-checkbox" type="checkbox" checked={showDayunCol} onChange={(e) => setShowDayunCol(e.target.checked)} /> 大运</label>
              <label><input className="styled-checkbox" type="checkbox" checked={showLiunianCol} onChange={(e) => setShowLiunianCol(e.target.checked)} /> 流年</label>
              <label><input className="styled-checkbox" type="checkbox" checked={showLiuyueCol} onChange={(e) => setShowLiuyueCol(e.target.checked)} /> 流月</label>
            </div>
          </div>
          <ChartTable
            baziResult={baziResult}
            riGan={riGan}
            diShiData={diShiData}
            naYinData={naYinData}
            dayunCol={showDayunCol ? dayunCol : null}
            liunianCol={showLiunianCol ? liunianCol : null}
            liuyueCol={showLiuyueCol ? liuyueCol : null}
          />
          {/* 大运 · 流年 · 流月（内嵌钻取，与命盘同卡联动） */}
          {dayunList.length > 0 && (
            <Drilldown
              dayunList={dayunList}
              qiyunAge={dayunData.qiyunAge}
              birthYear={record.gregorianDate?.year}
              riGan={riGan}
              currentYear={currentYear}
              currentDayunIndex={currentDayunIndex}
              selectedDayunIndex={selectedDayunIndex}
              onSelectDayun={handleSelectDayun}
              liunianList={dayunLiunianList}
              selectedYear={selectedYear}
              onSelectYear={handleSelectYear}
              liuyueList={liuyueData}
              selectedMonthIndex={selectedMonthIndex}
              onSelectMonth={handleSelectMonth}
            />
          )}
        </div>

        {/* 神煞 / 五行分析：tab 切换合并一卡 */}
        <div className="card" id="section-shensha">
          <div className="analysis-tabs">
            <button
              type="button"
              className={`analysis-tab ${analysisTab === 'shensha' ? 'active' : ''}`}
              onClick={() => setAnalysisTab('shensha')}
            >神煞</button>
            <button
              type="button"
              className={`analysis-tab ${analysisTab === 'wuxing' ? 'active' : ''}`}
              onClick={() => setAnalysisTab('wuxing')}
            >五行分析</button>
          </div>
          {analysisTab === 'shensha'
            ? <ShenShaCard baziResult={baziResult} shenshaData={shenshaData} bare />
            : <WuxingDisplay wuxing={wuxingData} riGan={riGan} />}
        </div>

        {/* AI 解析（预埋入口，功能开发中） */}
        <div className="card" id="section-ai">
          <div className="card-head-row">
            <h2>AI解析</h2>
          </div>
          <div className="ai-placeholder">
            <BrandIcon size={40} className="ai-placeholder-icon" />
            <div className="ai-placeholder-title">敬请期待</div>
            <p className="ai-placeholder-desc">结合八字命盘与大运流年，生成白话智能解读，正在打磨中。</p>
          </div>
        </div>

        <div id="section-comments">
          <CommentsSection baziId={record._id} />
        </div>

        {isOwner && showShareSettingsModal && (
          <div className="modal-backdrop" onClick={() => setShowShareSettingsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span>分享设置</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowShareSettingsModal(false)}>关闭</button>
              </div>
              <ShareSettingsSection record={record} onUpdated={(data) => setRecord((prev) => ({ ...prev, ...data }))} />
            </div>
          </div>
        )}

        {isOwner && showLabelsModal && (
          <div className="modal-backdrop" onClick={() => setShowLabelsModal(false)}>
            <div className="label-setting-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span>标签管理</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowLabelsModal(false)}>关闭</button>
              </div>
              <div className="label-setting-modal">
                <TagSelect
                  value={labelSelection}
                  onChange={(next) => setLabelSelection(next)}
                  placeholder="输入标签，按回车添加"
                  allowFreeText={true}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                  <button className="btn btn-secondary" onClick={() => setShowLabelsModal(false)}>取消</button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        const res = await baziAPI.update(record._id, { labels: labelSelection });
                        setRecord(res.data?.data || record);
                        setShowLabelsModal(false);
                      } catch (err) {
                        alert(err.response?.data?.message || '保存标签失败');
                      }
                    }}
                  >保存</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BaziDetail;
